import { createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { SellerCategory, VolumeOfPotential, YardType } from '../../core/models/Seller';
import FuelType from '../../core/models/FuelType';
import { commonApiSlice } from '../api/commonApiSlice';
import Transmission from '../../core/models/Transmission';
import VehicleCategory from '../../core/models/VehicleCategory';
import VehicleMake from '../../core/models/VehicleMake';
import VehicleModel from '../../core/models/VehicleModel';
import VehicleGrade from '../../core/models/VehicleGrade';
import VehicleColor from '../../core/models/VehicleColor';
import VehicleType from '../../core/models/VehicleType';
import VehicleCondition from '../../core/models/VehicleCondition';
import VehicleCylinder from '../../core/models/VehicleCylinder';
import VehicleLocation from '../../core/models/VehicleLocation';

interface CommonState {
    vehicleMakes: VehicleMake[] | null,
    vehicleModels: VehicleModel[] | null,
    vehicleGrades: VehicleGrade[] | null,
    vehicleColors: VehicleColor[] | null,
    vehicleTypes: VehicleType[] | null,
    vehicleConditions: VehicleCondition[] | null,
    vehicleCylinders: VehicleCylinder[] | null,
    vehicleLocations: VehicleLocation[] | null,
    vehicleCategories: VehicleCategory[] | null,
    fuelTypes: FuelType[] | null,
    transmissions: Transmission[] | null,
    sellerCategories: SellerCategory[] | null,
    yardTypes: YardType[] | null,
    volumeOfPotential: VolumeOfPotential[] | null
}

const initialState: CommonState = {
    vehicleMakes: null,
    vehicleModels: null,
    vehicleGrades: null,
    vehicleColors: null,
    vehicleTypes: null,
    vehicleConditions: null,
    vehicleCylinders: null,
    vehicleLocations: null,
    vehicleCategories: null,
    fuelTypes: null,
    transmissions: null,
    sellerCategories: null,
    yardTypes: null,
    volumeOfPotential: null
}

const commonSlice = createSlice({
    name: 'common',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleMakes.matchFulfilled,
            (state, { payload }) => {
                state.vehicleMakes = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleModels.matchFulfilled,
            (state, { payload }) => {
                state.vehicleModels = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleGrades.matchFulfilled,
            (state, { payload }) => {
                state.vehicleGrades = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleColors.matchFulfilled,
            (state, { payload }) => {
                state.vehicleColors = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleCylinders.matchFulfilled,
            (state, { payload }) => {
                state.vehicleCylinders = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleConditions.matchFulfilled,
            (state, { payload }) => {
                state.vehicleConditions = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleTypes.matchFulfilled,
            (state, { payload }) => {
                state.vehicleTypes = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleLocation.matchFulfilled,
            (state, { payload }) => {
                state.vehicleLocations = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVehicleCategories.matchFulfilled,
            (state, { payload }) => {
                state.vehicleCategories = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getFuelTypes.matchFulfilled,
            (state, { payload }) => {
                state.fuelTypes = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getTransmissions.matchFulfilled,
            (state, { payload }) => {
                state.transmissions = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getSellerCategories.matchFulfilled,
            (state, { payload }) => {
                state.sellerCategories = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getYardTypes.matchFulfilled,
            (state, { payload }) => {
                state.yardTypes = payload;
            }
        );
        builder.addMatcher(
            commonApiSlice.endpoints.getVolumeOfPotential.matchFulfilled,
            (state, { payload }) => {
                state.volumeOfPotential = payload;
            }
        );
    },
});

export default commonSlice.reducer

export const getVehicleMakes = (state: RootState): VehicleMake[] | null => state.common.vehicleMakes;
export const getVehicleModels = (state: RootState): VehicleModel[] | null => state.common.vehicleModels;
export const getVehicleGrades = (state: RootState): VehicleGrade[] | null => state.common.vehicleGrades;
export const getVehicleColors = (state: RootState): VehicleColor[] | null => state.common.vehicleColors;
export const getVehicleTypes = (state: RootState): VehicleType[] | null => state.common.vehicleTypes;
export const getVehicleConditions = (state: RootState): VehicleCondition[] | null => state.common.vehicleConditions;
export const getVehicleCylinders = (state: RootState): VehicleCylinder[] | null => state.common.vehicleCylinders;
export const getVehicleLocations = (state: RootState): VehicleLocation[] | null => state.common.vehicleLocations;
export const getVehicleCategories = (state: RootState): VehicleCategory[] | null => state.common.vehicleCategories;
export const getFuelTypes = (state: RootState): FuelType[] | null => state.common.fuelTypes;
export const getTransmissions = (state: RootState): Transmission[] | null => state.common.transmissions;
export const getSellerCategories = (state: RootState): SellerCategory[] | null => state.common.sellerCategories;
export const getYardTypes = (state: RootState): YardType[] | null => state.common.yardTypes;
export const getVolumeOfPotential = (state: RootState): VolumeOfPotential[] | null => state.common.volumeOfPotential;
